
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { Phone, AlertCircle, ArrowRight } from 'lucide-react'
import { Button, H2, Body, ButtonVariant, Input, INPUT_MASKS } from '@meiuca/ui-components'

const phoneSchema = z.object({
  phone: z.string()
    .min(1, 'Telefone é obrigatório')
    .transform((val) => val.replace(/\D/g, '')) // Remove tudo que não é dígito
    .refine((val) => val.length >= 10, 'Telefone deve ter pelo menos 10 dígitos')
    .refine((val) => val.length <= 11, 'Telefone deve ter no máximo 11 dígitos')
    .refine((val) => /^[1-9]{2}[2-9][0-9]{7,8}$/.test(val), 'Telefone inválido'),
})

type PhoneFormData = z.infer<typeof phoneSchema>

interface PhoneInputStepProps {
  onPhoneSubmit: (phone: string) => void
  onLoginClick?: () => void
  isLoading?: boolean
  error?: string | null
}

export function PhoneInputStep({
  onPhoneSubmit,
  onLoginClick,
  isLoading = false,
  error
}: PhoneInputStepProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
  })

  const onSubmit = (data: PhoneFormData) => {
    onPhoneSubmit(data.phone)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="text-center mb-8">
        <motion.div
          className="w-16 h-16 bg-gradient-to-br from-brand-secondary to-brand-primary rounded-2xl mx-auto mb-6 flex items-center justify-center"
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <span className="text-2xl font-bold text-white">M</span>
        </motion.div>
        
        <H2 className="mb-2">Vamos começar!</H2>
        <Body className="text-text-secondary">
          Digite seu telefone para criar sua conta
        </Body>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-background-negative border border-red-200 rounded-lg flex items-center gap-3"
        >
          <AlertCircle className="w-5 h-5 text-negative flex-shrink-0" />
          <Body className="text-negative text-sm">{error}</Body>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Phone Field */}
        <Input
          {...register('phone')}
          type="tel"
          label="Telefone *"
          placeholder="(11) 99999-9999"
          mask={INPUT_MASKS.PHONE_BR}
          unmask={true}
          leftIcon={Phone}
          error={!!errors.phone}
          errorMessage={errors.phone?.message}
          required
          fullWidth
          className="w-full"
          autoFocus
        />

        {/* Continue Button */}
        <Button
          variant={ButtonVariant.PRIMARY}
          type="submit"
          loading={isLoading}
          className="w-full"
        >
          {isLoading ? 'Verificando...' : (
            <>
              Continuar
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>

      {/* Login Link */}
      <div className="mt-8 text-center">
        <Body className="text-text-secondary">
          Já tem uma conta?{' '}
          <button
            onClick={onLoginClick}
            className="text-brand-primary hover:text-brand-secondary font-medium transition-colors"
          >
            Fazer login
          </button>
        </Body>
      </div>

      {/* Info */}
      <div className="mt-6 p-4 bg-background-secondary rounded-lg">
        <Body className="text-text-secondary text-sm text-center">
          Vamos enviar um código de verificação para confirmar seu telefone
        </Body>
      </div>
    </motion.div>
  )
}